import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { BudgetSheetState, DraftPatchPayload } from '../../../../../../../services/projectAutofillDraftService.ts'
import {
  formatPublishProjectAutofillDraftError,
  getProjectAutofillDraft,
  parsePublishProjectAutofillDraftResponse,
  patchProjectAutofillDraft,
  publishProjectAutofillDraft
} from '../../../../../../../services/projectAutofillDraftService.ts'
import type { ProjectAutofillDraftGuardApi } from '../../../../../../../contexts/ProjectAutofillDraftGuardContext.tsx'
import type {
  CommittedDraftSnapshot,
  DraftValidationShape,
  ProjectAutofillPayload,
  RubroRow
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import {
  getEffectiveDocumentationPageCount,
  rubroHasValidSelectedSkill,
  serializeRubrosSnapshot,
  validationBundleFromPatchResponse,
  validateRubrosDocumentationPages
} from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import type { ProjectAutofillEditorUiState } from '../../../../../../../utils/conversationalMode/projectAutofillEditorValidation.ts'
import {
  attachBudgetBindingToState,
  validateBudgetSheetStateClient
} from '../../../../../../../utils/conversationalMode/budgetSheetStateUtils.ts'
import { toHtmlDateInputValue } from '../../../../../../../utils/toHtmlDateInputValue.ts'
import { fireAnalytics } from '../../../../../../../utils/firebase'
import { getDeviceCategory, getSessionId } from '../../../../../../../utils/analyticsHelpers.ts'

type SaveState = 'idle' | 'ok' | 'error'

export type UseProjectAutofillSavePublishArgs = {
  draftId: string | undefined
  alreadyPublishedFromMetadata: boolean
  dirty: boolean
  fuzzyNeedsConfirm: boolean
  docPageValidationHint: string | null
  uploadingSlotKey: string | null
  rubros: RubroRow[]
  name: string
  description: string
  zone: string
  startDate: string
  endDate: string
  editorValidation: ProjectAutofillEditorUiState
  documentationPreview: Parameters<typeof getEffectiveDocumentationPageCount>[0]
  draftMetadataForPageCount: Parameters<typeof getEffectiveDocumentationPageCount>[1]
  anyRubroNeedsMasterPdf: boolean
  docPdfUrl: string
  buildSubprojectsPayload: (rows: RubroRow[]) => NonNullable<DraftPatchPayload['subprojects']>
  patchMetadataPayload: () => NonNullable<DraftPatchPayload['metadata']>
  setPatchedDraftSync: React.Dispatch<
    React.SetStateAction<{
      validation?: DraftValidationShape
      collectedData?: ProjectAutofillPayload['collectedData']
    } | null>
  >
  committedRef: React.MutableRefObject<CommittedDraftSnapshot | null>
  checkAccess: (action: string) => boolean
  serverBlockingIssuesNonEmpty: boolean
  publicationReadiness: DraftValidationShape['publicationReadiness']
  alreadyPublishedFromMetadata: boolean
  guardSetApi: ((api: ProjectAutofillDraftGuardApi | null) => void) | undefined
  guardBumpUi: (() => void) | undefined
  edifyUserId?: string
}

export function useProjectAutofillSavePublish(args: UseProjectAutofillSavePublishArgs) {
  const {
    draftId,
    alreadyPublishedFromMetadata,
    dirty,
    fuzzyNeedsConfirm,
    docPageValidationHint,
    uploadingSlotKey,
    rubros,
    name,
    description,
    zone,
    startDate,
    endDate,
    editorValidation,
    documentationPreview,
    draftMetadataForPageCount,
    anyRubroNeedsMasterPdf,
    docPdfUrl,
    buildSubprojectsPayload,
    patchMetadataPayload,
    setPatchedDraftSync,
    committedRef,
    checkAccess,
    serverBlockingIssuesNonEmpty,
    publicationReadiness,
    guardSetApi,
    guardBumpUi,
    edifyUserId = ''
  } = args

  const [isSaving, setIsSaving] = useState(false)
  const [saveState, setSaveState] = useState<SaveState>('idle')
  const [isPublishing, setIsPublishing] = useState(false)
  const [publishError, setPublishError] = useState<string | null>(null)
  const [publishSuccess, setPublishSuccess] = useState<{
    proyectoId: string
    obraName: string
    subproyectoCount: number
  } | null>(null)

  const draftLocked = alreadyPublishedFromMetadata || !!publishSuccess

  useEffect(() => {
    setPublishSuccess(null)
  }, [draftId])

  const applyPatchResponseToValidation = useCallback(async (res: unknown, id: string) => {
    let bundle = validationBundleFromPatchResponse(res)
    if (!bundle) {
      try {
        const fresh = await getProjectAutofillDraft(id)
        bundle = validationBundleFromPatchResponse(fresh)
      } catch {
      }
    }
    if (bundle) {
      setPatchedDraftSync(bundle)
    }
  }, [setPatchedDraftSync])

  const uploadBusy = uploadingSlotKey != null

  const canSaveDraft = useMemo(() => {
    if (!draftId) return false
    if (isSaving) return false
    if (!dirty) return false
    if (rubros.length < 1) return false
    if (fuzzyNeedsConfirm) return false
    if (docPageValidationHint) return false
    if (uploadBusy) return false
    if (draftLocked) return false
    if (editorValidation.pendingFields.length > 0) return false
    if (editorValidation.filteredBlockingIssues.length > 0) return false
    if (anyRubroNeedsMasterPdf && !docPdfUrl.trim()) return false
    if (!rubros.every((r) => rubroHasValidSelectedSkill(r))) return false
    return true
  }, [
    draftId,
    isSaving,
    dirty,
    rubros,
    fuzzyNeedsConfirm,
    docPageValidationHint,
    uploadBusy,
    draftLocked,
    editorValidation,
    anyRubroNeedsMasterPdf,
    docPdfUrl
  ])

  const performSave = useCallback(async (): Promise<boolean> => {
    if (!draftId || !dirty || fuzzyNeedsConfirm || uploadingSlotKey != null) return false
    if (rubros.length < 1) return false
    if (draftLocked) return false
    if (editorValidation.pendingFields.length > 0) return false
    if (editorValidation.filteredBlockingIssues.length > 0) return false
    if (anyRubroNeedsMasterPdf && !docPdfUrl.trim()) return false
    if (!rubros.every((r) => rubroHasValidSelectedSkill(r))) return false
    const maxPage = getEffectiveDocumentationPageCount(documentationPreview, draftMetadataForPageCount)
    if (maxPage != null) {
      const verr = validateRubrosDocumentationPages(rubros, maxPage)
      if (verr) {
        setSaveState('error')
        return false
      }
    }
    for (const r of rubros) {
      if (!r.budgetSheetState) continue
      const bound = attachBudgetBindingToState(
        {
          tempId: r.tempId,
          detectedLabel: r.detectedLabel,
          selectedSkillId: r.selectedSkillId
        },
        JSON.parse(JSON.stringify(r.budgetSheetState)) as BudgetSheetState
      )
      const verrBudget = validateBudgetSheetStateClient(bound)
      if (verrBudget) {
        setSaveState('error')
        return false
      }
    }
    setIsSaving(true)
    setSaveState('idle')
    try {
      const ns = toHtmlDateInputValue(startDate)
      const ne = toHtmlDateInputValue(endDate)
      const patchBody = {
        project: {
          name: name || undefined,
          description: description.trim() ? description : undefined,
          zone: zone.trim() ? zone : undefined,
          startDate: ns || undefined,
          endDate: ne || undefined
        },
        subprojects: buildSubprojectsPayload(rubros),
        metadata: patchMetadataPayload()
      }
      const res = await patchProjectAutofillDraft(String(draftId), patchBody)
      await applyPatchResponseToValidation(res, String(draftId))
      committedRef.current = {
        name,
        description,
        zone,
        startDate: ns,
        endDate: ne,
        rubrosKey: serializeRubrosSnapshot(rubros),
        rubrosCount: rubros.length
      }
      setSaveState('ok')
      return true
    } catch {
      setSaveState('error')
      return false
    } finally {
      setIsSaving(false)
    }
  }, [
    draftId,
    dirty,
    fuzzyNeedsConfirm,
    name,
    description,
    zone,
    startDate,
    endDate,
    rubros,
    applyPatchResponseToValidation,
    documentationPreview,
    draftMetadataForPageCount,
    patchMetadataPayload,
    uploadingSlotKey,
    draftLocked,
    editorValidation,
    anyRubroNeedsMasterPdf,
    docPdfUrl,
    buildSubprojectsPayload,
    committedRef
  ])

  const performSaveRef = useRef(performSave)
  performSaveRef.current = performSave

  const canPublishDraft = useMemo(() => {
    if (!draftId) return false
    if (rubros.length < 1) return false
    if (!rubros.every((r) => rubroHasValidSelectedSkill(r))) return false
    if (serverBlockingIssuesNonEmpty) return false
    if (publicationReadiness?.readyForCommitSequence !== true) return false
    if (alreadyPublishedFromMetadata) return false
    if (dirty) return false
    if (fuzzyNeedsConfirm) return false
    if (docPageValidationHint) return false
    if (uploadBusy) return false
    if (isSaving) return false
    if (isPublishing) return false
    if (publishSuccess) return false
    if (draftLocked) return false
    return true
  }, [
    draftId,
    rubros,
    serverBlockingIssuesNonEmpty,
    publicationReadiness?.readyForCommitSequence,
    alreadyPublishedFromMetadata,
    dirty,
    fuzzyNeedsConfirm,
    docPageValidationHint,
    uploadBusy,
    isSaving,
    isPublishing,
    publishSuccess,
    draftLocked
  ])

  const publishSnapRef = useRef({ canPublishDraft, isPublishing })
  publishSnapRef.current = { canPublishDraft, isPublishing }

  const handlePublish = useCallback(async () => {
    if (!checkAccess('publish_private_project')) return
    if (!draftId || !canPublishDraft || rubros.length < 1) return
    setPublishError(null)
    setIsPublishing(true)
    try {
      const res = await publishProjectAutofillDraft(String(draftId))
      const parsed = parsePublishProjectAutofillDraftResponse(res)
      const pid = parsed.proyectoId?.trim()
      if (!pid) {
        setPublishError('No se recibió el id del proyecto.')
        return
      }
      setPublishSuccess({
        proyectoId: pid,
        obraName: name.trim(),
        subproyectoCount: parsed.subproyectoIds?.length ?? rubros.length
      })
      fireAnalytics('private_obra_autofill_published', {
        edify_user_id: edifyUserId,
        session_id: getSessionId(),
        device_category: getDeviceCategory(),
        category: 'obras_privadas',
        action: 'private_obra_autofill_published',
        draft_id: String(draftId),
        proyecto_id: pid,
        subproyecto_count: parsed.subproyectoIds?.length ?? rubros.length
      })
    } catch (e) {
      setPublishError(formatPublishProjectAutofillDraftError(e))
    } finally {
      setIsPublishing(false)
    }
  }, [checkAccess, draftId, canPublishDraft, name, rubros.length, edifyUserId])

  const handlePublishRef = useRef(handlePublish)
  handlePublishRef.current = handlePublish

  const guardSnapRef = useRef({
    dirty,
    draftId: draftId as string | undefined,
    fuzzyNeedsConfirm,
    isSaving,
    uploadBusy: false,
    rubrosCount: rubros.length,
    canSaveDraft: false
  })
  guardSnapRef.current = {
    dirty,
    draftId,
    fuzzyNeedsConfirm,
    isSaving,
    uploadBusy,
    rubrosCount: rubros.length,
    canSaveDraft
  }

  useEffect(() => {
    if (!guardSetApi) {
      return
    }
    if (!draftId) {
      guardSetApi(null)
      return
    }
    const api: ProjectAutofillDraftGuardApi = {
      isDirty: () => guardSnapRef.current.dirty,
      canPersist: () => guardSnapRef.current.canSaveDraft,
      saveDraft: () => performSaveRef.current(),
      canPublish: () => publishSnapRef.current.canPublishDraft,
      publishDraft: async () => {
        await handlePublishRef.current()
      },
      isPublishing: () => publishSnapRef.current.isPublishing
    }
    guardSetApi(api)
    return () => {
      guardSetApi(null)
    }
  }, [guardSetApi, draftId, canPublishDraft, isPublishing, canSaveDraft])

  useEffect(() => {
    guardBumpUi?.()
  }, [guardBumpUi, canPublishDraft, isPublishing, publishSuccess, alreadyPublishedFromMetadata, draftLocked])

  const handleSave = () => {
    void performSave()
  }

  return {
    isSaving,
    saveState,
    setSaveState,
    isPublishing,
    publishError,
    publishSuccess,
    draftLocked,
    canSaveDraft,
    canPublishDraft,
    performSave,
    handleSave,
    handlePublish,
    applyPatchResponseToValidation
  }
}
